import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useUser, useClerk } from "@clerk/clerk-react";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { addInvitedUser, removeInvitedUser } from "../../store/userSlice";

// This would be replaced with an actual API call in a real implementation
const createEnterpriseUser = async (email: string) => {
  // In a real application, you'd make a call to your backend API
  // e.g.: await fetch('/api/users/enterprise/invite', { method: 'POST', ... })
  
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Simulate success
  return { success: true, message: 'User created successfully' };
};

const UserInvitation: React.FC = () => {
  const { user: clerkUser } = useUser();
  const { client: clerkClient } = useClerk();
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  // Get invited users from Redux store
  const invitedUsers = user?.invitedUsers || [];

  const handleInviteUser = async () => {
    if (!email || !email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      // First create the user in the backend
      await createEnterpriseUser(email);

      // Then use Clerk to send the invitation
      // In a real implementation with Clerk Organizations:
      // await clerkClient.organizations.createOrganizationInvitation({...})
      
      // For mock purposes we'll just add to Redux
      dispatch(addInvitedUser({ email, status: "Pending" }));
      
      setSuccess(true);
      setEmail("");
    } catch (err) {
      setError("Failed to send invitation. Please try again.");
      console.error("Invitation error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (email: string) => {
    setUserToDelete(email);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      // In a real implementation with Clerk Organizations:
      // await clerkClient.organizations.revokeInvitation({...})
      
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Remove from Redux store
      dispatch(removeInvitedUser(userToDelete));
      
      setSuccess(true);
    } catch (err) {
      setError("Failed to remove user. Please try again.");
      console.error("Delete error:", err);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };

  // Only admin users can access this feature
  const isAdmin = user?.role === 'ADMIN';

  if (!isAdmin) {
    return (
      <Card sx={{ boxShadow: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" component="div">
            User Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You don't have permission to manage users. Contact your administrator for access.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ boxShadow: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" component="div" sx={{ mb: 2 }}>
            Invite Users
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Invite team members to join your enterprise account
          </Typography>
          
          <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
            <TextField
              label="Email Address"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              disabled={loading}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleInviteUser}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              Invite
            </Button>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="h6" component="div" sx={{ mb: 2 }}>
            Pending Invitations & Team Members
          </Typography>
          
          {invitedUsers.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No pending invitations or team members yet
            </Typography>
          ) : (
            <List>
              {invitedUsers.map((invitedUser, index) => (
                <React.Fragment key={invitedUser.id || invitedUser.email}>
                  <ListItem>
                    <ListItemText 
                      primary={invitedUser.email} 
                      secondary={invitedUser.status} 
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteUser(invitedUser.email)}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < invitedUsers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      
      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success">
          Operation completed successfully
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {userToDelete}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteUser} 
            color="error" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserInvitation; 